import React, { useState, useEffect, useMemo } from "react";
import { FiFilter } from "react-icons/fi";
import { getApiInstance } from "../../../../../utils/api";
import Table from "../../../../../components/table/table";
import InputDropdown from "../../../../../components/inputs/inputDropdown/inputDropdown";
import { LuEye } from "react-icons/lu";
import Button from "../../../../../components/button/button";
import InputSearch from "../../../../../components/inputs/inputSearch/inputSearch";
import Badge from "../../../../../components/badge/badge";
import "./versionesAnteriores.scss";
import Tag from "../../../../../components/tag/tag";
import Accordion from "../../../../../components/accordion/accordion";
import Modal from "../../../../../components/modal/modal";
import { useNavigate } from "react-router-dom";
import { BiExport } from "react-icons/bi";
import ImageRenderer from "../../../../../components/ImageRender/ImageRender";
import { matchesSearchInObject } from "../../../../../utils/searchUtils";

const tipoCambioLabels = {
    1: "Nueva familia",
    2: "Familia modificada",
    3: "Familia eliminada",
};

const tipoCambioVariants = {
    1: "success",
    2: "warning",
    3: "danger",
};

const columnsSinEstado = [
    {
        key: "rutaImagen",
        label: "Miniatura",
        render: (row) => (
            <ImageRenderer
                thumbnail={row.rutaImagen}
                alt={row.nombreBook || "Sin nombre"}
            />
        ),
    },
    {
        key: "tipoCambio",
        label: "Tipo de Cambio",
        render: (row) => {
            let variant;

            switch (row.tipoCambio) {
                case "Nueva familia":
                    variant = "success";
                    break;
                case "Familia modificada":
                    variant = "warning";
                    break;
                case "Familia eliminada":
                    variant = "danger";
                    break;
                default:
                    variant = "default";
            }

            return <Badge label={row.tipoCambio || "N/A"} variant={variant} />;
        },
    },
    { key: "nombreBook", label: "Nombre", className: "col-nombre" },
    { key: "referencia", label: "Referencia" },
    { key: "medida", label: "Unidad de medida" },
    { key: "nombreFamilia", label: "Familia" },
    {
        key: "variante",
        label: "Variante",
        render: (row) => row.variante || "N/A",
    },
    {
        key: "nombreFamilia",
        label: "Categoría",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
];

const familiaMapping = {
    1: "Mobiliario Central",
    2: "Mobiliario Perimetral",
    3: "Mesas",
    4: "Probadores",
    5: "Caja",
    6: "Dotación",
    7: "Instalaciones",
    8: "Señalética",
    9: "BOH",
    10: "Detalles constructivos",
11: "Detalles instalaciones",
12: "Montaje de mobiliario",
};

export default function VersionesAnteriores() {
    const [searchTerm, setSearchTerm] = useState("");
    const [versionOptions, setVersionOptions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [mueblesPorVersion, setMueblesPorVersion] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState(null);
    const [filterVariantes, setFilterVariantes] = useState([]);
    const [filterCategoria, setFilterCategorias] = useState([]);
    const [filterSubcategoria, setFilterSubcategoria] = useState([]);
    const [variantes, setVariantes] = useState([]);
    const [subCategorias, setSubcategorias] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [familias, setFamilias] = useState([]);
    const [filterFamilia, setFilterFamilia] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);

    const api = getApiInstance();
    const navigate = useNavigate();

    const fetchVersiones = async () => {
        try {
            const response = await api.get("/api/Book/GetAllBooks");

            const versions = response.data
                .map((book) => ({
                    label: `Versión v.${
                        book.version
                    } - Publicado el: ${new Date(
                        book.publicado
                    ).toLocaleDateString()}`,
                    value: book.codBook,
                }))
                .reverse();

            setVersionOptions(versions);
        } catch (error) {
            console.error("Error al obtener las versiones", error);
        }
    };

    const fetchMueblesPorVersion = async (codBook) => {
        try {
            const response = await api.get(
                `/api/Mueble/GetMueblesPorVersion/${codBook}`
            );
            setMueblesPorVersion(response.data);
        } catch (error) {
            console.error(
                "Error al obtener los muebles con cambios por versión",
                error
            );
            setMueblesPorVersion([]);
        }
    };

    const handleVersionSelect = (option) => {
        const selected = versionOptions.find((v) => v.value === option.value);
        if (selected) {
            setSelectedVersion(selected.label);
            fetchMueblesPorVersion(option.value);
        }
    };

    useEffect(() => {
        fetchVersiones();
    }, []);

    const handleSearch = (query) => {
        setSearchTerm(query);
        setCurrentPage(1); // Resetear a página 1 cuando se haga una nueva búsqueda
    };

    useEffect(() => {
        console.log(mueblesPorVersion);
        const allVariantes = mueblesPorVersion
            .map((product) => product.variante) // Extraer variantes de cada producto
            .flat() // Aplanar el array (porque variantes es un array dentro de cada producto)
            .filter((value, index, self) => self.indexOf(value) === index); // Filtrar duplicados

        // Filtrar subcategorías sin duplicados
        const allSubcategorias = mueblesPorVersion
            .map((product) => product.categorias)
            .flat()
            .filter(
                (value, index, self) =>
                    self.indexOf(value) === index && value != null
            );

        // Filtrar categorías sin duplicados
        const allCategorias = mueblesPorVersion
            .map((product) => familiaMapping[product.codFamilia])
            .filter((value, index, self) => self.indexOf(value) === index);

        const allFamilias = mueblesPorVersion
            .map((product) => product.nombreFamilia)
            .filter(
                (value, index, self) =>
                    self.indexOf(value) === index && value != null
            ); // Filtrar duplicados

        // Actualizar el estado
        setVariantes(allVariantes);
        setSubcategorias(allSubcategorias);
        setCategorias(allCategorias);
        setFamilias(allFamilias);
    }, [mueblesPorVersion]);

    // Filtrar y ordenar muebles
    const filteredMuebles = useMemo(() => {
        let filtered = mueblesPorVersion.filter((mueble) =>
            matchesSearchInObject(mueble, searchTerm)
        );

        if (sortOrder === "A-Z") {
            return filtered.sort((a, b) =>
                a.nombreBook.localeCompare(b.nombreBook)
            );
        } else if (sortOrder === "Z-A") {
            return filtered.sort((a, b) =>
                b.nombreBook.localeCompare(a.nombreBook)
            );
        }

        return (
            filtered
                .filter(
                    (product) =>
                        filterVariantes.length === 0 ||
                        filterVariantes.includes(product.variante)
                )
                .filter(
                    (product) =>
                        filterCategoria.length === 0 ||
                        filterCategoria.includes(
                            familiaMapping[product.codFamilia]
                        )
                )
                // .filter(
                //     (product) =>
                //         filterSubcategoria.length === 0 ||
                //         product.categorias.some((categoria) =>
                //             filterSubcategoria.includes(categoria)
                //         )
                // )
                .filter(
                    (product) =>
                        filterFamilia.length === 0 ||
                        filterFamilia.includes(product.nombreFamilia)
                )
        );
    }, [
        searchTerm,
        mueblesPorVersion,
        sortOrder,
        filterVariantes,
        filterCategoria,
        filterFamilia,
    ]);

    const actionsVersionesAnteriores = (row) => [
        {
            label: "Ver cambios",
            onClick: () =>
                navigate(`/ficha-producto/${row.codMueble}?tab=historico`),
            icon: <LuEye />,
        },
    ];

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleSortChange = (order) => {
        setSortOrder((prev) => (prev === order ? null : order));
    };

    const handleAcceptFilters = () => {
        closeModal();
        setCurrentPage(1); // Resetear a página 1 cuando se apliquen filtros
    };

    const handleCleanFilter = () => {
        setFilterVariantes([]);
        setFilterCategorias([]);
        setFilterSubcategoria([]);
        setFilterFamilia([]);
        setCurrentPage(1); // Resetear a página 1 cuando se limpien filtros
    };

    const handleSelectVariante = (varianteName) => {
        setFilterVariantes((prevSelected) => {
            // Verificar si la variante ya está en la lista
            if (prevSelected.includes(varianteName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (variante) => variante !== varianteName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, varianteName];
            }
        });
    };

    const handleSelectCategoria = (categoriaName) => {
        setFilterCategorias((prevSelected) => {
            // Verificar si la categoria ya está en la lista
            if (prevSelected.includes(categoriaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (categoria) => categoria !== categoriaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, categoriaName];
            }
        });
    };

    const handleSelectSubcategoria = (subcategoriaName) => {
        setFilterSubcategoria((prevSelected) => {
            // Verificar si la subcategoria ya está en la lista
            if (prevSelected.includes(subcategoriaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (subcategoria) => subcategoria !== subcategoriaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, subcategoriaName];
            }
        });
    };

    const handleSelectFamilia = (familiaName) => {
        setFilterFamilia((prevSelected) => {
            // Verificar si la familia ya está en la lista
            if (prevSelected.includes(familiaName)) {
                // Si ya está en la lista, eliminarla
                return prevSelected.filter(
                    (familia) => familia !== familiaName
                );
            } else {
                // Si no está, agregarla
                return [...prevSelected, familiaName];
            }
        });
    };
    const handleDownloadPlanosYExcelZip = async () => {
        if (mueblesPorVersion.length === 0) {
            // Validar que haya muebles seleccionados
            alert(
                "Selecciona al menos un mueble para exportar los planos y el Excel."
            );
            return;
        }

        try {
            const response = await api.post(
                "/api/Mueble/DownloadPlanosYExcelZip", // Endpoint de exportación
                mueblesPorVersion.map((mueble) => mueble.codMueble), // IDs seleccionados
                {
                    responseType: "blob", // Manejo de la respuesta como archivo
                }
            );

            // Crear enlace de descarga
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "PlanosYExcelMuebles.zip");
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Error al exportar los planos y Excel:", error);
            alert("Hubo un error al intentar exportar los planos y el Excel.");
        }
    };

    return (
        <>
            <div className="dashboard-productos__content__table-header">
                <InputSearch
                    context="tableSearch"
                    placeholder="Buscar..."
                    onSearch={handleSearch}
                />

                    <InputDropdown
                        options={versionOptions}
                        onSelect={handleVersionSelect}
                        placeholder="Selecciona una versión"
                        showLabel={false}
                        value={{ label: selectedVersion }}
                    />
                <div className="dashboard-productos__content__table-header__buttons">
                    <Button
                        label="Filtros"
                        onClick={openModal}
                        icon={<FiFilter />}
                    />
                    <Button
                        label="Exportar"
                        variant="outline"
                        icon={<BiExport />}
                        onClick={handleDownloadPlanosYExcelZip}
                        disabled={mueblesPorVersion.length === 0} // Deshabilitar si no hay muebles seleccionados
                    />
                </div>
            </div>
            <Table
                columns={columnsSinEstado}
                data={filteredMuebles || []}
                noResultsMessage="No se han encontrado muebles para la versión seleccionada."
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                resetPaginationOnDataChange={false}
            />

            <Modal
                title="Filtros"
                isOpen={isModalOpen}
                onClose={closeModal}
                onAccept={handleAcceptFilters}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredMuebles.length})`}
                onCancel={handleCleanFilter}
            >
                <div>
                    <Accordion title="Variante">
                        <ul className="section-header__modal__section__content">
                            {variantes.map((familia, index) => (
                                <Tag
                                    key={index}
                                    label={familia}
                                    isSelected={filterVariantes.includes(
                                        familia
                                    )}
                                    onSelect={handleSelectVariante}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Categoría">
                        <ul className="section-header__modal__section__content">
                            {categorias.map((categoria, index) => (
                                <Tag
                                    key={index}
                                    label={categoria}
                                    isSelected={filterCategoria.includes(
                                        categoria
                                    )}
                                    onSelect={handleSelectCategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                {/* <div className="section-header__modal__section">
					<Accordion title="Subcategoría">
						<ul className="section-header__modal__section__content">
							{subCategorias.map((subcategoria, index) => (
								<Tag
									key={index}
									label={subcategoria}
									isSelected={filterSubcategoria.includes(subcategoria)}
									onSelect={handleSelectSubcategoria}
								/>
							))}
						</ul>
					</Accordion>
				</div> */}
                <div className="section-header__modal__section">
                    <Accordion title="Familia">
                        <ul className="section-header__modal__section__content">
                            {familias.map((familia, index) => (
                                <Tag
                                    key={index}
                                    label={familia}
                                    isSelected={filterFamilia.includes(familia)}
                                    onSelect={handleSelectFamilia}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
            </Modal>
        </>
    );
}
